<template>
  <div>
    <v-container class="mb-2" fluid>
      <ui-filter v-model="filter" :fields="filterFields" @apply="filterApplyHandle" />
    </v-container>

    <v-container v-if="showSummary" class="pt-0 pb-2" fluid>
      <v-row dense align="stretch" no-gutters class="workout-summary-row">
        <v-col
          v-for="summaryItem in summaryItems"
          :key="summaryItem.label"
          class="pa-1 workout-summary-col"
        >
          <v-card outlined class="workout-summary-card">
            <v-card-text class="py-3">
              <div class="text-caption grey--text text--lighten-1">{{ summaryItem.label }}</div>
              <div class="text-subtitle-1 font-weight-medium">{{ summaryItem.value }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="auto" class="pa-1 d-flex align-stretch">
          <v-btn
            color="success"
            class="workout-summary-download"
            :disabled="!summaryItemsSource.length"
            icon
            title="Скачать CSV"
            @click="downloadSummaryCsv"
          >
            <v-icon>mdi-file-delimited-outline</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-data-table
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
    >
      <template v-slot:[`item.title`]="{ item }">
        <label-link :to="{ name: 'GymEdit', params: { id: item.gym.id } }"> {{ item.gym.title }}</label-link>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-chip :color="getWorkoutStatusColor(item.status)">{{ getWorkoutStatusText(item.status) }}</v-chip>
      </template>

      <template v-slot:[`item.date_start`]="{ item }">
        {{ vFormatDate(item.date_start, "DD.MM.YYYY") }}
      </template>

      <template v-slot:[`item.user_full_name`]="{ item }">
        <label-link :to="{ name: 'UserEdit', params: { id: item.user.id } }"> {{ vGetFullName(item) }}</label-link>
      </template>

      <template v-slot:[`item.workout_duration`]="{ item }">
        {{ vFormatDurations(item.date_start, getWorkoutEndTime(item)) }}
      </template>

      <template v-slot:[`item.price`]="{ item }">
        {{ formatCurrency(item.price) }}
      </template>

      <template v-slot:[`item.time_start`]="{ item }">
        {{ vFormatDate(item.date_start, "HH:mm") }}
      </template>

      <template v-slot:[`item.finished_at`]="{ item }">
        {{ vFormatDate(getWorkoutEndTime(item), "HH:mm") }}
      </template>

      <template v-slot:[`item.products`]="{ item }">
        <template v-if="item.machineOrders.length">
          <v-row v-for="product in vGetProducts(item.machineOrders)" :key="product.id" dense :title="product.title">
            <v-col>{{ product.name }}</v-col>
          </v-row>
        </template>
        <span v-else>-</span>
      </template>

      <template v-slot:[`item.products_cost`]="{ item }">
        {{ vGetProductsSum(item.machineOrders) }}
      </template>

      <template v-slot:[`item.workout_extend`]="{ item }">
        {{ vGetExtendedTime(item.extends) }}
      </template>

      <template v-slot:[`item.workout_pretend`]="{ item }">
        {{ vGetReducedTime(item.finished_at, item.date_end) }}
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-end">
          <v-tooltip v-if="canManualFinishWorkout(item)" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                class="mr-2"
                color="success"
                v-bind="attrs"
                v-on="on"
                @click="onManualFinishWorkout(item)"
              >
                mdi-check-circle-outline
              </v-icon>
            </template>
            Завершить вручную
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="mr-2" v-bind="attrs" v-on="on" @click="onShowPhotos(item)">mdi-camera-image</v-icon>
            </template>
            Фотографии зала
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <GalleryStatusGym v-model="galleryStatusDialog" :images-after="imagesAfter" :images-before="imagesBefore" />
  </div>
</template>

<script>
import { UserOptions, Filterable, Sortable, EntityIndex, WorkoutStatus, Notify } from "@/mixins";
import { UiFilter, LabelLink } from "@/components/UI";
import { formatDateUnix, timestampDuration } from "@/utils";
import GalleryStatusGym from "../dialogs/GalleryStatusGym.vue";

export default {
  mixins: [UserOptions, Filterable, Sortable, EntityIndex, WorkoutStatus, Notify],
  components: {
    UiFilter,
    GalleryStatusGym,
    LabelLink,
  },
  props: {
    fetchItems: {
      type: Function,
      required: true,
      default: () => {},
    },
    fetchSummaryItems: {
      type: Function,
      default: null,
    },
    showSummary: {
      type: Boolean,
      default: false,
    },
    showGymFilter: {
      type: Boolean,
      default: false,
    },
    showManualFinish: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      policyKey: "workout_policy",
      galleryStatusDialog: false,
      items: [],
      summarySourceItems: [],
      total: 0,
      loading: false,
      chosenItem: null,
      gyms: [],
      finishingWorkoutId: null,
    };
  },
  computed: {
    headers() {
      return [
        { text: "Id", value: "id", width: 60, sortable: false },
        { text: "Зал", value: "title", width: 160, sortable: false },
        { text: "Статус", value: "status", width: 100, sortable: false },
        { text: "Дата", value: "date_start", width: 120 },
        { text: "Пользователь", value: "user_full_name", width: 180 },
        { text: "Продолжительность", value: "workout_duration", width: 130 },
        { text: "Стоимость", value: "price", width: 140 },
        { text: "Время начала", value: "time_start", width: 100, sortable: false },
        { text: "Время конца", value: "finished_at", width: 100, sortable: false },
        { text: "Продукты", value: "products", width: 200, sortable: false },
        { text: "Стоимость продуктов", value: "products_cost", width: 140, sortable: false },
        { text: "Продленные минуты", value: "workout_extend", width: 100 },
        { text: "Сокращенные минуты", value: "workout_pretend", width: 100 },
        { text: "Действия", value: "actions", sortable: false, align: "end" },
      ];
    },
    imagesBefore() {
      return this.chosenItem?.problems?.flatMap((item) => item.photos.filter((photo) => !!photo.src)) ?? [];
    },
    imagesAfter() {
      return this.chosenItem?.statusPhotos?.filter((item) => !!item.src) ?? [];
    },
    summaryItems() {
      const workoutCount = this.summaryItemsSource.length;
      const totalPrice = this.summaryItemsSource.reduce((acc, curr) => acc + (curr.price ?? 0), 0);
      const totalProductsCost = this.summaryItemsSource.reduce(
        (acc, curr) => acc + this.getProductsSumValue(curr.machineOrders),
        0
      );
      const totalDurationMinutes = this.summaryItemsSource.reduce(
        (acc, curr) => acc + this.getDurationMinutes(curr.date_start, this.getWorkoutEndTime(curr)),
        0
      );
      const totalExtendedMinutes = this.summaryItemsSource.reduce(
        (acc, curr) => acc + this.getExtendedMinutes(curr.extends),
        0
      );
      const totalReducedMinutes = this.summaryItemsSource.reduce(
        (acc, curr) => acc + this.getReducedMinutes(curr.finished_at, curr.date_end),
        0
      );

      return [
        { label: "Бронирований", value: workoutCount.toLocaleString("ru") },
        { label: "Общая стоимость", value: this.formatCurrency(totalPrice) },
        { label: "Стоимость продуктов", value: this.formatCurrency(totalProductsCost) },
        { label: "Общая длительность", value: this.formatMinutes(totalDurationMinutes) },
        { label: "Продленные минуты", value: this.formatMinutes(totalExtendedMinutes) },
        { label: "Сокращенные минуты", value: this.formatMinutes(totalReducedMinutes) },
      ];
    },
    summaryItemsSource() {
      return this.summarySourceItems.length ? this.summarySourceItems : this.items;
    },
    filterFields() {
      const fields = [
        {
          label: "Статус",
          id: "status",
          component: "VSelect",
          props: {
            items: this.WorkoutStatuses,
          },
        },
        {
          label: "Дата начала",
          id: "date_start",
          component: "DatePicker",
        },
        {
          label: "Дата конца",
          id: "date_end",
          component: "DatePicker",
        },
        {
          label: "Стоимость",
          id: "price",
          component: "NumberInputRange",
        },
      ];

      if (this.showGymFilter) {
        fields.unshift({
          label: "Зал",
          id: "gym_id",
          component: "VSelect",
          props: {
            items: this.gyms,
          },
        });
      }

      return fields;
    },
  },
  mounted() {
    if (this.showGymFilter) {
      this.fetchGyms();
    }
  },
  watch: {
    options: {
      async handler() {
        await this.fetch();
      },
      deep: true,
    },
  },
  methods: {
    vGetFullName(item) {
      let result = "";

      if (item.user?.last_name) {
        result += item.user.last_name;
      }
      if (item.user?.first_name) {
        result += result ? ` ${item.user.first_name}` : item.user.first_name;
      }
      if (item.user?.second_name) {
        result += result ? ` ${item.user.second_name}` : item.user.second_name;
      }
      return result || "Пользователь";
    },
    getDurationMinutes(dateStart, finishEnd) {
      return dateStart && finishEnd && dateStart < finishEnd ? timestampDuration(dateStart, finishEnd) : 0;
    },
    getWorkoutEndTime(item) {
      return item?.finished_at || item?.date_end || null;
    },
    vFormatDurations(dateStart, finishEnd) {
      const minutes = this.getDurationMinutes(dateStart, finishEnd);
      return minutes > 0 ? `${minutes}м` : "-";
    },
    vFormatDate(date, format = "DD.MM.YYYY") {
      return formatDateUnix(date, format);
    },
    vGetProducts(orders = []) {
      return orders.flatMap((item) =>
        item.items ? JSON.parse(item.items).map((product, index) => ({ ...product, id: `${item.id}_${index}` })) : []
      );
    },
    getExtendedMinutes(extendItems = []) {
      const totalSeconds = extendItems.reduce((acc, curr) => acc + curr.seconds, 0);
      return totalSeconds > 0 ? Math.ceil(totalSeconds / 60) : 0;
    },
    vGetExtendedTime(extendItems = []) {
      const minutes = this.getExtendedMinutes(extendItems);
      return minutes > 0 ? `${minutes}м` : "-";
    },
    getReducedMinutes(finishedAt, dateEnd) {
      return finishedAt && dateEnd && finishedAt < dateEnd ? timestampDuration(finishedAt, dateEnd) : 0;
    },
    vGetReducedTime(finishedAt, dateEnd) {
      const minutes = this.getReducedMinutes(finishedAt, dateEnd);
      return minutes > 0 ? `${minutes}м` : "-";
    },
    getProductsSumValue(products = []) {
      return products.reduce((acc, curr) => acc + (curr.amount ?? 0), 0);
    },
    vGetProductsSum(products = []) {
      const sum = this.getProductsSumValue(products);
      return sum ? this.formatCurrency(sum) : "-";
    },
    formatCurrency(value = 0) {
      return `${value.toLocaleString("ru")} ₽`;
    },
    formatMinutes(value = 0) {
      return `${value.toLocaleString("ru")} м`;
    },
    getApiErrorMessage(exception, fallbackText = "Не удалось выполнить действие") {
      return (
        exception?.response?.data?.errors?.[0]?.message ||
        exception?.message ||
        fallbackText
      );
    },
    canManualFinishWorkout(item) {
      return (
        this.showManualFinish &&
        this.userCanUpdate &&
        this.finishingWorkoutId !== item.id &&
        item.status === this.WORKOUT_STATUS_PROCESS
      );
    },
    escapeCsvValue(value) {
      const stringValue = value === null || value === undefined ? "" : String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    },
    getWorkoutStatusLabel(status) {
      return this.getWorkoutStatusText(status);
    },
    getWorkoutCsvRow(item) {
      return [
        item.id,
        item.gym?.title ?? "",
        this.getWorkoutStatusLabel(item.status),
        this.vFormatDate(item.date_start, "DD.MM.YYYY"),
        this.vGetFullName(item),
        this.getDurationMinutes(item.date_start, this.getWorkoutEndTime(item)),
        item.price ?? 0,
        this.vFormatDate(item.date_start, "HH:mm"),
        this.vFormatDate(this.getWorkoutEndTime(item), "HH:mm"),
        this.vGetProducts(item.machineOrders).map((product) => product.name).join(", "),
        this.getProductsSumValue(item.machineOrders),
        this.getExtendedMinutes(item.extends),
        this.getReducedMinutes(item.finished_at, item.date_end),
      ];
    },
    downloadFile(content, fileName, mimeType) {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    downloadSummaryCsv() {
      const headers = [
        "ID",
        "Зал",
        "Статус",
        "Дата",
        "Пользователь",
        "Продолжительность (мин)",
        "Стоимость",
        "Время начала",
        "Время конца",
        "Продукты",
        "Стоимость продуктов",
        "Продленные минуты",
        "Сокращенные минуты",
      ];
      const rows = this.summaryItemsSource.map((item) => this.getWorkoutCsvRow(item));
      const totals = [
        "Итого",
        "",
        "",
        "",
        "",
        this.summaryItemsSource.reduce(
          (acc, curr) => acc + this.getDurationMinutes(curr.date_start, this.getWorkoutEndTime(curr)),
          0
        ),
        this.summaryItemsSource.reduce((acc, curr) => acc + (curr.price ?? 0), 0),
        "",
        "",
        "",
        this.summaryItemsSource.reduce(
          (acc, curr) => acc + this.getProductsSumValue(curr.machineOrders),
          0
        ),
        this.summaryItemsSource.reduce(
          (acc, curr) => acc + this.getExtendedMinutes(curr.extends),
          0
        ),
        this.summaryItemsSource.reduce(
          (acc, curr) => acc + this.getReducedMinutes(curr.finished_at, curr.date_end),
          0
        ),
      ];

      const csvContent = [headers, ...rows, totals]
        .map((row) => row.map((value) => this.escapeCsvValue(value)).join(";"))
        .join("\r\n");

      this.downloadFile(
        `\uFEFF${csvContent}`,
        `workouts-${this.vFormatDate(Math.floor(Date.now() / 1000), "YYYY-MM-DD_HH-mm")}.csv`,
        "text/csv;charset=utf-8;"
      );
    },
    async fetchGyms() {
      const result = await this.$api.gym.gyms();

      if (result.data?.data?.gyms) {
        this.gyms = result.data.data.gyms.map((item) => ({
          text: item.title,
          value: item.id,
        }));
      }
    },
    async fetchSummary(payload) {
      if (!this.showSummary) {
        return;
      }

      if (this.fetchSummaryItems) {
        this.summarySourceItems = await this.fetchSummaryItems(payload);
        return;
      }

      this.summarySourceItems = [...this.items];
    },
    async fetch() {
      // eslint-disable-next-line no-unused-vars
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;

      this.loading = true;

      const filter = this.getFilterInput(this.filter, this.getFilterMap(this.filterFields));
      const input = { filter };
      const orderBy = this.getSortInput(sortBy, sortDesc);

      if (orderBy) {
        input.orderBy = orderBy;
      }

      const workoutsData = await this.fetchItems({ page, itemsPerPage, input });

      if (workoutsData) {
        this.items = workoutsData.data;
        this.total = workoutsData.paginatorInfo?.total;
      }

      await this.fetchSummary({ page, itemsPerPage, input, total: this.total });

      this.loading = false;
    },
    filterApplyHandle() {
      this.fetch();
    },
    async onManualFinishWorkout(item) {
      if (!window.confirm(`Завершить бронирование #${item.id}?`)) {
        return;
      }

      try {
        this.finishingWorkoutId = item.id;
        await this.$api.workout.adminFinishWorkout(item.id);
        this.notifySuccess(`Бронирование #${item.id} завершено.`);
        await this.fetch();
      } catch (exception) {
        this.notifyError(this.getApiErrorMessage(exception));
      } finally {
        this.finishingWorkoutId = null;
      }
    },
    onShowPhotos(item) {
      this.chosenItem = item;
      this.galleryStatusDialog = !this.galleryStatusDialog;
    },
  },
};
</script>

<style scoped>
.workout-summary-row {
  flex-wrap: nowrap;
  width: 100%;
}

.workout-summary-col {
  flex: 1 1 0;
  min-width: 0;
}

.workout-summary-card {
  height: 100%;
}

.workout-summary-download {
  width: 76px;
  min-width: 76px;
  height: 76px;
  margin-top: 4px;
}

.workout-summary-download .v-icon {
  margin: 0;
}
</style>
